import { useState, useEffect, useRef, useCallback } from 'react';
import createGlobe from 'cobe';
import { projectToScreen, greatCirclePoints, phiForLongitude } from '../../../utils/mapProjection';
import { getCoords } from '../../../utils/airportCoords';

// Demo routes from DXB
const ROUTES = [
  { from: 'DXB', to: 'LHR' },
  { from: 'DXB', to: 'BKK' },
  { from: 'DXB', to: 'JFK' },
  { from: 'DXB', to: 'NRT' },
];

const RESOLVED = ROUTES.map((r) => ({
  ...r,
  fromCoords: getCoords(r.from)!,
  toCoords: getCoords(r.to)!,
}));

const AIRPORTS = [...new Set(ROUTES.flatMap((r) => [r.from, r.to]))];
const RESOLVED_AIRPORTS = AIRPORTS.map((a) => ({ iata: a, coords: getCoords(a)! })).filter((a) => a.coords);

const BASE = getCoords('DXB')!;
const INITIAL_PHI = phiForLongitude(BASE.lon);
const INITIAL_THETA = Math.max(-0.5, Math.min(0.5, (BASE.lat * Math.PI / 180) * 0.4));

const GLOBE_SIZE = 260;

interface Props {
  active: boolean;
}

export function GlobeSlide({ active }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arcCanvasRef = useRef<HTMLCanvasElement>(null);
  const globeRef = useRef<ReturnType<typeof createGlobe> | null>(null);
  const [webglFailed, setWebglFailed] = useState(false);

  const drawOverlay = useCallback(
    (canvas: HTMLCanvasElement, phi: number, theta: number, size: number, progress: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let ri = 0; ri < RESOLVED.length; ri++) {
        const route = RESOLVED[ri];
        const arcStart = ri * 0.15;
        const arcProgress = Math.max(0, Math.min(1, (progress - arcStart) / 0.4));
        if (arcProgress <= 0) continue;

        const allPoints = greatCirclePoints(
          route.fromCoords.lat, route.fromCoords.lon,
          route.toCoords.lat, route.toCoords.lon, 80,
        );
        const pointCount = Math.ceil(allPoints.length * arcProgress);
        const points = allPoints.slice(0, pointCount);

        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];
        for (const p of points) {
          const { x, y, visible } = projectToScreen(p.lat, p.lon, phi, theta, size);
          if (visible) current.push({ x: x * dpr, y: y * dpr });
          else if (current.length > 0) { segments.push(current); current = []; }
        }
        if (current.length > 0) segments.push(current);

        for (const seg of segments) {
          if (seg.length < 2) continue;
          ctx.beginPath();
          ctx.moveTo(seg[0].x, seg[0].y);
          for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
          ctx.strokeStyle = 'rgba(201, 160, 220, 0.25)';
          ctx.lineWidth = 6 * dpr;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(seg[0].x, seg[0].y);
          for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
          ctx.strokeStyle = `rgba(201, 160, 220, ${0.5 + arcProgress * 0.4})`;
          ctx.lineWidth = 2 * dpr;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
      }

      for (const ap of RESOLVED_AIRPORTS) {
        const dotStart = ap.iata === 'DXB' ? 0 : 0.3;
        const dotAlpha = Math.max(0, Math.min(1, (progress - dotStart) / 0.2));
        if (dotAlpha <= 0) continue;

        const { x, y, visible } = projectToScreen(ap.coords.lat, ap.coords.lon, phi, theta, size);
        if (!visible) continue;

        const px = x * dpr;
        const py = y * dpr;
        const isBase = ap.iata === 'DXB';
        const dotR = (isBase ? 4 : 2.5) * dpr;

        if (isBase) {
          ctx.beginPath();
          ctx.arc(px, py, dotR + 3 * dpr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(201, 160, 220, ${0.3 * dotAlpha})`;
          ctx.lineWidth = 1.5 * dpr;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(px, py, dotR, 0, Math.PI * 2);
        ctx.fillStyle = isBase
          ? `rgba(201, 160, 220, ${dotAlpha})`
          : `rgba(201, 160, 220, ${0.7 * dotAlpha})`;
        ctx.fill();

        ctx.font = `600 ${9 * dpr}px "DM Sans", sans-serif`;
        ctx.fillStyle = `rgba(201, 160, 220, ${0.8 * dotAlpha})`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(ap.iata, px + dotR + 4 * dpr, py);
      }
    },
    [],
  );

  // WebGL globe
  useEffect(() => {
    const canvas = canvasRef.current;
    const arcCanvas = arcCanvasRef.current;
    if (!canvas || !arcCanvas || !active) return;

    const container = canvas.parentElement;
    if (!container) return;

    const size = GLOBE_SIZE;
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    arcCanvas.width = size * dpr;
    arcCanvas.height = size * dpr;
    arcCanvas.style.width = `${size}px`;
    arcCanvas.style.height = `${size}px`;

    const startTime = Date.now();

    let globe: ReturnType<typeof createGlobe>;
    try {
      globe = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: size * dpr,
        height: size * dpr,
        phi: INITIAL_PHI,
        theta: INITIAL_THETA,
        dark: 1,
        diffuse: 3,
        mapSamples: dpr >= 2 ? 30000 : 18000,
        mapBrightness: 8,
        mapBaseBrightness: 0.02,
        baseColor: [0.15, 0.12, 0.2],
        markerColor: [0.79, 0.63, 0.86],
        glowColor: [0.08, 0.06, 0.12],
        markers: [],
        scale: 1,
        offset: [0, 0],
        onRender: (state) => {
          state.phi = INITIAL_PHI - (Date.now() - startTime) * 0.00008;
          state.theta = INITIAL_THETA;
          const progress = Math.min(1, (Date.now() - startTime) / 3000);
          drawOverlay(arcCanvas, state.phi, INITIAL_THETA, size, progress);
        },
      });
      globeRef.current = globe;
    } catch {
      setWebglFailed(true);
      return;
    }

    return () => {
      globe?.destroy();
      globeRef.current = null;
    };
  }, [active, drawOverlay]);

  // Fallback: draw arcs without WebGL globe
  useEffect(() => {
    if (!webglFailed || !active) return;
    const arcCanvas = arcCanvasRef.current;
    if (!arcCanvas) return;

    const size = GLOBE_SIZE;
    const dpr = window.devicePixelRatio || 1;
    arcCanvas.width = size * dpr;
    arcCanvas.height = size * dpr;
    arcCanvas.style.width = `${size}px`;
    arcCanvas.style.height = `${size}px`;

    const startTime = Date.now();
    let raf: number;
    const draw = () => {
      const elapsed = Date.now() - startTime;
      const phi = INITIAL_PHI - elapsed * 0.00008;
      const progress = Math.min(1, elapsed / 3000);

      // drawOverlay clears canvas and draws arcs
      drawOverlay(arcCanvas, phi, INITIAL_THETA, size, progress);

      // Draw globe outline on top (after drawOverlay's clearRect)
      const ctx = arcCanvas.getContext('2d');
      if (ctx) {
        const r = 0.8 * size / 2 * dpr;
        const cx = (size / 2) * dpr;
        const cy = (size / 2) * dpr;
        // Subtle filled circle
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, 'rgba(201, 160, 220, 0.03)');
        grad.addColorStop(0.8, 'rgba(201, 160, 220, 0.02)');
        grad.addColorStop(1, 'rgba(201, 160, 220, 0)');
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        // Outline ring
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(201, 160, 220, 0.1)';
        ctx.lineWidth = 1 * dpr;
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [webglFailed, active, drawOverlay]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8">
      {/* Globe */}
      <div
        className="relative mb-6"
        style={{ animation: active ? 'ob-fade-in 0.8s ease-out both' : undefined }}
      >
        <div className="relative" style={{ width: GLOBE_SIZE, height: GLOBE_SIZE }}>
          {!webglFailed && (
            <canvas ref={canvasRef} className="w-full h-full" style={{ contain: 'layout size' }} />
          )}
          <canvas
            ref={arcCanvasRef}
            className={`${webglFailed ? '' : 'absolute inset-0'} w-full h-full pointer-events-none`}
          />
        </div>
        {/* Ambient glow behind globe */}
        <div
          className="absolute inset-0 -z-10 rounded-full blur-[60px]"
          style={{
            background: 'radial-gradient(circle, rgba(201,160,220,0.15) 0%, transparent 70%)',
            transform: 'scale(1.3)',
          }}
        />
      </div>

      {/* Text */}
      <h1
        className="text-[28px] leading-tight font-extrabold text-slate-100 mb-3 text-center"
        style={{ animation: active ? 'ob-slide-up 0.6s ease-out 0.4s both' : undefined }}
      >
        Your roster, reimagined
      </h1>
      <p
        className="text-[15px] text-slate-400 text-center max-w-[280px] leading-relaxed"
        style={{ animation: active ? 'ob-slide-up 0.6s ease-out 0.6s both' : undefined }}
      >
        Import once. See your flights, crew, and layovers — everywhere.
      </p>
    </div>
  );
}
